/**
 * Reference data from CoinGecko's public API.
 *
 * CoinGecko is kept only for what DefiLlama does not cover — coin and exchange
 * listings, per-exchange tickers, and fiat rates. All of these still work
 * without an API key; only deep price history was paywalled behind the
 * 365-day window (error 10012), and that moved to DefiLlama. See
 * `src/data/prices.ts`.
 *
 * The free tier rate-limits hard: roughly six requests in twenty seconds
 * returns 429. Everything here is reference data that changes slowly, so it is
 * cached aggressively by the query layer.
 */
const COINGECKO = "https://api.coingecko.com/api/v3";

export class MetadataLookupError extends Error {
  constructor(
    message: string,
    readonly cause?: unknown,
  ) {
    super(message);
    this.name = "MetadataLookupError";
  }
}

async function coingecko<T>(path: string): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${COINGECKO}${path}`);
  } catch (cause) {
    throw new MetadataLookupError(`Network error reaching CoinGecko (${path})`, cause);
  }

  if (response.status === 429) {
    throw new MetadataLookupError(
      "CoinGecko rate limit hit. Reference data is cached — this usually means it was requested too eagerly.",
    );
  }
  if (!response.ok) {
    throw new MetadataLookupError(`CoinGecko returned ${response.status} for ${path}`);
  }

  return (await response.json()) as T;
}

export interface CoinSummary {
  /** CoinGecko id, e.g. "bitcoin" — the value stored in `Trade.coinBase`. */
  id: string;
  symbol: string;
  name: string;
  image?: string;
}

export interface ExchangeSummary {
  id: string;
  name: string;
}

/**
 * Quote currencies a given exchange lists for a given base coin.
 *
 * Drives the cascading dropdowns on the trade form.
 */
export async function getExchangeQuotes(
  exchangeId: string,
  coinId: string,
): Promise<{ id: string; label: string }[]> {
  const body = await coingecko<{
    tickers?: { base?: string; target?: string }[];
  }>(`/exchanges/${encodeURIComponent(exchangeId)}/tickers?coin_ids=${encodeURIComponent(coinId)}`);

  const seen = new Map<string, { id: string; label: string }>();
  for (const ticker of body.tickers ?? []) {
    if (!ticker.base || !ticker.target) continue;
    seen.set(ticker.target, {
      id: ticker.target,
      label: `${ticker.base}/${ticker.target}`,
    });
  }

  return [...seen.values()];
}

/**
 * Fiat units per 1 USD, e.g. `eur -> 0.88`.
 *
 * CoinGecko quotes everything against BTC, so each fiat rate is divided by the
 * USD rate to rebase. Only needed for the one production trade quoted in EUR,
 * and for preferred-currency display.
 */
export async function getFiatPerUsd(): Promise<Map<string, number>> {
  const body = await coingecko<{
    rates?: Record<string, { value?: number; type?: string }>;
  }>("/exchange_rates");

  const rates = body.rates ?? {};
  const usd = rates["usd"]?.value;
  if (!usd) throw new MetadataLookupError("CoinGecko exchange_rates omitted a USD rate");

  const out = new Map<string, number>();
  for (const [code, rate] of Object.entries(rates)) {
    if (rate.type !== "fiat" || typeof rate.value !== "number") continue;
    out.set(code, rate.value / usd);
  }

  return out;
}

/** Top coins by market cap — the trade form's coin picker. */
export async function getTopCoins(count = 250): Promise<CoinSummary[]> {
  const body = await coingecko<
    { id: string; symbol: string; name: string; image?: string }[]
  >(`/coins/markets?vs_currency=usd&order=market_cap_desc&per_page=${count}&page=1&sparkline=false`);

  return body.map(({ id, symbol, name, image }) => ({ id, symbol, name, image }));
}

export async function getExchanges(): Promise<ExchangeSummary[]> {
  const body = await coingecko<{ id: string; name: string }[]>("/exchanges/list");
  return body.map(({ id, name }) => ({ id, name }));
}
